import { InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import AccessKeys from '@zeus/core/AccessKeys';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { COLORS } from '_/lib/frontend-utils';
import { EFileType } from '_/types/common.types';
import {
  EDestinationType,
  IDestination,
  ECompressionType,
  AzureCredentialsType,
  IAzureCredentials,
} from '_/types/destinations/destinations.types';
import { Form, Input, Select, Checkbox, Button, Space, Alert, Typography, Popover, Radio, Row, Col } from 'antd';
import React, { ReactElement, useState, useCallback, useMemo, useEffect, ChangeEvent } from 'react';

import DestinationFormPopover from './components/DestinationFormPopover';
import { createDestinationAdapter, updateDestinationAdapter } from './DestinationForm.adapter';
import { INITIAL_DESTINATION, ZIP_FILE_TYPES } from './DestinationForms.constants';
import { useFileTypeHandlers } from './DestinationForms.hooks';
import { IDestinationForm } from './DestinationForms.types';

const DESTINATION_TYPE_OPTIONS = [
  { label: 'S3', value: EDestinationType.S3 },
  { label: 'SFTP', value: EDestinationType.SFTP },
  { label: 'Azure', value: EDestinationType.AZURE },
];

// Inline styles from DestinationFileTypes
const typographyStyle = {
  margin: 16,
};

const marginTopStyle = {
  marginTop: 30,
};

interface DestinationFormPageProps {
  mode: 'create' | 'edit';
  destination?: IDestination | null;
  onSubmit: (destination: any) => void;
  isSubmitting: boolean;
}

const DestinationFormPage = ({ mode, destination, onSubmit, isSubmitting }: DestinationFormPageProps): ReactElement => {
  const [form] = Form.useForm();
  const history = useNavigate();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { setBreadcrumbs } = useTopHeader();

  const [saveErrors, setSaveErrors] = useState<any[]>([]);
  const [openPopover, setOpenPopover] = useState(false);

  // State for SFTP popover
  const [openFolderPopover, setOpenFolderPopover] = useState(false);

  // Process initial values for edit mode
  const initialFormValues = useMemo(() => {
    if (mode === 'edit' && destination) {
      const { files } = destination;
      const { noCompression = false } = files;
      const processedDestination = {
        ...destination,
        compressionType: noCompression ? ECompressionType.NONE : ECompressionType.GZIP,
      };

      const destFileTypes = Object.keys(processedDestination.files);
      if (!noCompression && destFileTypes.length) {
        const selectedFileTypes = destFileTypes.filter((key) => processedDestination.files[key] === true);
        processedDestination.compressionType = selectedFileTypes.some((type) => ZIP_FILE_TYPES.includes(type))
          ? ECompressionType.ZIP
          : ECompressionType.GZIP;
      }

      processedDestination.fileType = processedDestination.files?.archive ? EFileType.ARCHIVE : EFileType.INDIVIDUAL;

      if (processedDestination.fileType === EFileType.ARCHIVE) {
        processedDestination.files = processedDestination.files.archive;
      }

      return processedDestination as IDestinationForm;
    }
    return INITIAL_DESTINATION;
  }, [mode, destination]);

  const [formValues, setFormValues] = useState<IDestinationForm>(initialFormValues);

  // State for Azure auth type - defaults to Account Key, derived from the saved destination in edit mode
  const getSavedAuthType = (values: IDestinationForm) => {
    const encryptedCfg = values.encryptedConfig as IAzureCredentials;
    return encryptedCfg?.[AzureCredentialsType.SAS_TOKEN] !== undefined && !encryptedCfg?.[AzureCredentialsType.ACCOUNT_KEY]
      ? AzureCredentialsType.SAS_TOKEN
      : AzureCredentialsType.ACCOUNT_KEY;
  };
  const [authType, setAuthType] = useState<AzureCredentialsType>(() => getSavedAuthType(initialFormValues));

  // Update form values when destination changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && destination) {
      const processed = initialFormValues;
      setFormValues(processed);
      form.setFieldsValue(processed);
      setAuthType(getSavedAuthType(processed));
    }
  }, [mode, destination, initialFormValues, form]);

  // Set up breadcrumbs
  useEffect(() => {
    const isEditMode = mode === 'edit';
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Destinations', href: `/orgs/${selectedOrgSlug}/destinations` },
      { title: isEditMode ? destination?.name || 'Edit Destination' : 'New Destination' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [mode, destination, selectedOrganization, selectedOrgSlug, setBreadcrumbs]);

  // Update form values on any change
  const handleValuesChange = (_: any, allValues: any) => {
    setFormValues((prev) => ({
      ...prev,
      ...allValues,
    }));
  };

  // Stable setValue function for nested config updates
  const setValue = useCallback(
    (field: keyof IDestinationForm, value: any) => {
      setFormValues((prev) => ({ ...prev, [field]: value }));
      form.setFieldValue(field, value);
    },
    [form],
  );

  // File type handlers
  const { setCompressionType, toggleFileFormatType, toggleFileType } = useFileTypeHandlers(setValue, formValues.files);

  // Config change handlers (inlined from useDestinationConfigHandler)
  const onConfigChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { id, value } = target;
      setValue('configuration', {
        ...formValues.configuration,
        [id]: value,
      });
    },
    [formValues.configuration, setValue],
  );

  const onEncryptedConfigChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { id, value } = target;
      setValue('encryptedConfig', {
        ...formValues.encryptedConfig,
        [id]: value,
      });
    },
    [formValues.encryptedConfig, setValue],
  );

  const handleAuthTypeChange = useCallback(
    (e: any) => {
      const newAuthType = e.target.value;
      setAuthType(newAuthType);
      setValue('encryptedConfig', {
        [newAuthType === AzureCredentialsType.ACCOUNT_KEY ? AzureCredentialsType.SAS_TOKEN : AzureCredentialsType.ACCOUNT_KEY]:
          '',
      });
    },
    [setValue],
  );

  const onFinish = async (values: any) => {
    setSaveErrors([]);

    try {
      const destinationData = {
        ...formValues,
        ...values,
      };

      const dto = mode === 'create' ? createDestinationAdapter(destinationData) : updateDestinationAdapter(destinationData);

      onSubmit(dto);
    } catch (error: any) {
      console.error('Error saving destination:', error);
      setSaveErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
    }
  };

  const onFinishFailed = () => {
    // Validation failed
  };

  const onCancel = () => {
    void history({ to: `/orgs/${selectedOrgSlug}/destinations` });
  };

  const handlePopover = () => {
    setOpenPopover(true);
  };

  const closePopover = () => {
    setOpenPopover(false);
  };

  // Check if save should be disabled
  const { configuration, compressionType, encryptedConfig = {}, files, name, type } = formValues;
  const { account, bucket, container, folder, host } = configuration as Record<string, string>;
  const { accountKey, sasToken } = encryptedConfig as Record<string, string>;

  let typeDisabled = false;
  switch (type) {
    case EDestinationType.AZURE:
      // For edit mode, credentials are optional if already set
      typeDisabled = !account || !container || (mode === 'create' && !accountKey && !sasToken);
      break;
    case EDestinationType.SFTP:
      typeDisabled = !folder && !host && !Object.keys(encryptedConfig).length;
      break;
    case EDestinationType.S3:
      // Only bucket is required, access keys are optional (can use IAM roles)
      typeDisabled = !bucket;
      break;
    default:
      typeDisabled = true;
  }

  const saveDisabled = !name || !compressionType || !type || typeDisabled || Object.values(files).every((val) => !val);

  // Filter errors by type
  const apiErrors = saveErrors.filter((e: any) => e.type === 'api' || !e.type);

  const isEditMode = mode === 'edit';

  return (
    <>
      {apiErrors.length > 0 && (
        <Alert
          message="Error"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {apiErrors.map((error: any, idx: number) => (
                <li key={idx}>{error.error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={handleValuesChange}
        initialValues={initialFormValues}
      >
        <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please input the destination name!' }]}>
          <Input placeholder="Destination name" />
        </Form.Item>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: 'Please select destination type!' }]}
            style={{ flex: 1 }}
          >
            <Select placeholder="Select Type" disabled={isEditMode}>
              {DESTINATION_TYPE_OPTIONS.map((option) => (
                <Select.Option value={option.value} key={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="active" valuePropName="checked" style={{ marginTop: 30 }}>
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </div>

        {type && (
          <div style={marginTopStyle}>
            {type !== EDestinationType.SFTP && (
              <Form.Item
                label="File Type:"
                name="fileType"
                style={marginTopStyle}
                rules={[{ required: true, message: 'Please select a file type' }]}
              >
                <Radio.Group value={formValues.fileType || 'individual'} onChange={toggleFileFormatType}>
                  <Radio value="individual">Individual Files</Radio>
                  <Radio value="archive">Archive</Radio>
                </Radio.Group>
              </Form.Item>
            )}
            <Form.Item
              label="Files:"
              style={marginTopStyle}
              rules={[
                {
                  validator: () => {
                    if (Object.values(files).some((val) => !!val)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please select at least one file type'));
                  },
                },
              ]}
            >
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Checkbox
                    checked={!!files.parquet}
                    onChange={toggleFileType}
                    id="parquet"
                    data-test-id="parquet-checkbox-clicked"
                  >
                    Parquet
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                      checked={!!files.custom || !!files.customZip}
                      onChange={toggleFileType}
                      id="custom"
                      data-test-id="custom-checkbox-clicked"
                    >
                      Custom Text
                    </Checkbox>
                    <Popover
                      open={openPopover}
                      onOpenChange={(visible) => {
                        if (!visible) closePopover();
                      }}
                      content={
                        <Typography.Text style={typographyStyle}>
                          Custom Text and Custom Parquet output settings can be configured on the collection configuration page
                        </Typography.Text>
                      }
                      placement="right"
                    >
                      <Button
                        type="text"
                        shape="circle"
                        onClick={handlePopover}
                        icon={<InfoCircleOutlined style={{ color: COLORS.DARK_BLUE }} />}
                      />
                    </Popover>
                  </div>
                </Col>
                <Col span={6}>
                  <Checkbox
                    checked={!!files.statsJson || !!files.statsJsonZip}
                    onChange={toggleFileType}
                    id="statsJson"
                    data-test-id="stats-json-checkbox-clicked"
                  >
                    Stats (JSON)
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox
                    checked={!!files.files}
                    onChange={toggleFileType}
                    id="files"
                    data-test-id="stats-json-checkbox-clicked"
                  >
                    Downloaded Files/Images
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox
                    checked={!!files.json || !!files.jsonZip}
                    onChange={toggleFileType}
                    id="json"
                    data-test-id="json-checkbox-clicked"
                  >
                    JSON
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox
                    checked={!!files.customParquet}
                    onChange={toggleFileType}
                    id="customParquet"
                    data-test-id="custom-parquet-checkbox-clicked"
                  >
                    Custom Parquet
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox
                    checked={!!files.pagesJson || !!files.pagesJsonZip}
                    onChange={toggleFileType}
                    id="pagesJson"
                    data-test-id="stats-json-checkbox-clicked"
                  >
                    Pages (JSON)
                  </Checkbox>
                </Col>
              </Row>
            </Form.Item>
            {formValues.fileType === EFileType.INDIVIDUAL && (
              <Form.Item
                label="Compression Options:"
                name="compressionType"
                style={marginTopStyle}
                rules={[{ required: true, message: 'Please select a compression option' }]}
              >
                <Radio.Group value={compressionType || 'gzip'} onChange={setCompressionType}>
                  <Radio value="gzip">gzip (default)</Radio>
                  <Radio value="zip">zip</Radio>
                  <Radio value="none">none</Radio>
                </Radio.Group>
              </Form.Item>
            )}
          </div>
        )}

        {/* Destination-specific configuration (inlined from useDestinationTypeForm) */}
        {type === EDestinationType.S3 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 16 }}>Configuration:</div>
            <Form.Item label="S3 Configuration">
              <Space>
                <Space.Compact>
                  <Input value="s3://" disabled style={{ width: 60 }} />
                  <Form.Item
                    name={['configuration', 'bucket']}
                    noStyle
                    rules={[
                      { required: true, message: 'Bucket is required' },
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 255, message: 'Bucket name cannot exceed 255 characters' },
                    ]}
                  >
                    <Input id="bucket" placeholder="Bucket" onChange={onConfigChange} style={{ width: 200 }} />
                  </Form.Item>
                  <Input value="/" disabled style={{ width: 30 }} />
                  <Form.Item
                    name={['configuration', 'template']}
                    noStyle
                    rules={[
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 500, message: 'Path cannot exceed 500 characters' },
                    ]}
                  >
                    <Input id="template" placeholder="Path Template" onChange={onConfigChange} style={{ width: 200 }} />
                  </Form.Item>
                  <Input value="/" disabled style={{ width: 30 }} />
                  <Form.Item
                    name={['configuration', 'filenameTemplate']}
                    noStyle
                    rules={[
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 255, message: 'Filename cannot exceed 255 characters' },
                    ]}
                  >
                    <Input
                      id="filenameTemplate"
                      placeholder=":snapshot_id.:ext"
                      onChange={onConfigChange}
                      style={{ width: 200 }}
                    />
                  </Form.Item>
                </Space.Compact>
                <DestinationFormPopover />
              </Space>
            </Form.Item>
            <AccessKeys encryptedConfig={formValues.encryptedConfig} handleEncryptedConfig={onEncryptedConfigChange} />
          </div>
        )}

        {type === EDestinationType.SFTP && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 16 }}>Configuration:</div>
            <Form.Item label="SFTP Path">
              <Space>
                <Space.Compact>
                  <Form.Item
                    name={['configuration', 'template']}
                    noStyle
                    rules={[
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 500, message: 'Path cannot exceed 500 characters' },
                    ]}
                  >
                    <Input id="template" placeholder="Path Template" onChange={onConfigChange} style={{ width: 250 }} />
                  </Form.Item>
                  <Input value="/" disabled style={{ width: 30 }} />
                  <Form.Item
                    name={['configuration', 'filenameTemplate']}
                    noStyle
                    rules={[
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 255, message: 'Filename cannot exceed 255 characters' },
                    ]}
                  >
                    <Input
                      id="filenameTemplate"
                      placeholder=":snapshot_id.:ext"
                      onChange={onConfigChange}
                      style={{ width: 250 }}
                    />
                  </Form.Item>
                </Space.Compact>
                <Popover
                  open={openFolderPopover}
                  onOpenChange={(visible) => setOpenFolderPopover(visible)}
                  content={
                    <Typography.Text style={{ margin: 16 }}>
                      Available template variables: <br />
                      :start_YY, :start_YYYY, :start_M, :start_MM, :start_D, :start_DD,
                      <br />
                      :end_YY, :end_YYYY, :end_M, :end_MM, :end_D, :end_DD, :org, <br />
                      :project, :source, :collection, :snapshot_id, :rows, :ext.
                      <br />
                      <br />
                      Variables can be surrounded by curly braces, e.g. : {'{start_YYYY}'}.
                    </Typography.Text>
                  }
                  placement="right"
                >
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() => setOpenFolderPopover(true)}
                    icon={<InfoCircleOutlined style={{ color: COLORS.DARK_BLUE }} />}
                  />
                </Popover>
              </Space>
            </Form.Item>
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 5 }}>
              <Form.Item
                label="Host"
                name={['configuration', 'host']}
                rules={[{ required: true, message: 'Host is required' }]}
                style={{ minHeight: 60, marginTop: 10 }}
              >
                <Input id="host" placeholder="Host" onChange={onConfigChange} value={formValues.configuration?.host || ''} />
              </Form.Item>
              <Form.Item label="Port" name={['configuration', 'port']} style={{ minHeight: 60, marginTop: 10 }}>
                <Input id="port" placeholder="Port" onChange={onConfigChange} value={formValues.configuration?.port || ''} />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 5 }}>
              <div style={{ marginTop: 20 }}>
                <span style={{ fontWeight: 'bold' }}>
                  SFTP Access Information (if any of these values are given, all are overwritten):
                </span>
              </div>
              <Form.Item label="Username" name={['encryptedConfig', 'username']} style={{ minHeight: 60, marginTop: 10 }}>
                <Input.Password
                  id="username"
                  placeholder="Username"
                  onChange={onEncryptedConfigChange}
                  value={formValues.encryptedConfig?.username || ''}
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item label="Password" name={['encryptedConfig', 'password']} style={{ minHeight: 60, marginTop: 10 }}>
                <Input.Password
                  id="password"
                  placeholder="Password"
                  onChange={onEncryptedConfigChange}
                  value={formValues.encryptedConfig?.password || ''}
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item label="SSH Key" name={['encryptedConfig', 'privateKey']} style={{ minHeight: 60, marginTop: 10 }}>
                <Input.Password
                  id="privateKey"
                  placeholder="SSH Key"
                  onChange={onEncryptedConfigChange}
                  value={formValues.encryptedConfig?.privateKey || ''}
                  autoComplete="off"
                />
              </Form.Item>
            </div>
          </div>
        )}

        {type === EDestinationType.AZURE && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 'bold', marginTop: 20, marginBottom: 16 }}>Configuration:</div>
            <Form.Item label="Azure Storage Configuration">
              <Space>
                <Space.Compact>
                  <Input value="https://" disabled style={{ width: 70 }} />
                  <Form.Item
                    name={['configuration', 'account']}
                    noStyle
                    rules={[
                      { required: true, message: 'Account is required' },
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 255, message: 'Account name cannot exceed 255 characters' },
                    ]}
                  >
                    <Input id="account" placeholder="Account" onChange={onConfigChange} style={{ width: 150 }} />
                  </Form.Item>
                  <Input value=".core.windows.net/" disabled style={{ width: 140 }} />
                  <Form.Item
                    name={['configuration', 'container']}
                    noStyle
                    rules={[
                      { required: true, message: 'Container is required' },
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 255, message: 'Container name cannot exceed 255 characters' },
                    ]}
                  >
                    <Input id="container" placeholder="Container" onChange={onConfigChange} style={{ width: 150 }} />
                  </Form.Item>
                  <Input value="/" disabled style={{ width: 30 }} />
                  <Form.Item
                    name={['configuration', 'template']}
                    noStyle
                    rules={[
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 500, message: 'Path cannot exceed 500 characters' },
                    ]}
                  >
                    <Input id="template" placeholder="Path Template" onChange={onConfigChange} style={{ width: 150 }} />
                  </Form.Item>
                  <Input value="/" disabled style={{ width: 30 }} />
                  <Form.Item
                    name={['configuration', 'filenameTemplate']}
                    noStyle
                    rules={[
                      { whitespace: true, message: 'Cannot contain only whitespace' },
                      { max: 255, message: 'Filename cannot exceed 255 characters' },
                    ]}
                  >
                    <Input
                      id="filenameTemplate"
                      placeholder=":snapshot_id.:ext"
                      onChange={onConfigChange}
                      style={{ width: 150 }}
                    />
                  </Form.Item>
                </Space.Compact>
                <DestinationFormPopover />
              </Space>
            </Form.Item>
            <Typography.Text strong style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
              Authentication Type:
            </Typography.Text>
            <Form.Item style={{ marginTop: 10 }}>
              <Radio.Group value={authType} onChange={handleAuthTypeChange}>
                <Radio value={AzureCredentialsType.ACCOUNT_KEY}>Account Key</Radio>
                <Radio value={AzureCredentialsType.SAS_TOKEN}>Shared Access Signature Token</Radio>
              </Radio.Group>
            </Form.Item>
            <Typography.Text strong style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
              If the value is given, it will be overwritten:
            </Typography.Text>
            {authType === AzureCredentialsType.ACCOUNT_KEY && (
              <Form.Item
                label="Account key"
                name={['encryptedConfig', AzureCredentialsType.ACCOUNT_KEY]}
                style={{ marginTop: 10, maxWidth: 300 }}
              >
                <Input.Password
                  id={AzureCredentialsType.ACCOUNT_KEY}
                  placeholder="Account key"
                  onChange={onEncryptedConfigChange}
                  value={(formValues.encryptedConfig as IAzureCredentials)?.[AzureCredentialsType.ACCOUNT_KEY] ?? ''}
                />
              </Form.Item>
            )}
            {authType === AzureCredentialsType.SAS_TOKEN && (
              <Form.Item
                label="SAS Token"
                name={['encryptedConfig', AzureCredentialsType.SAS_TOKEN]}
                style={{ marginTop: 10, maxWidth: 300 }}
              >
                <Input.Password
                  id={AzureCredentialsType.SAS_TOKEN}
                  placeholder="SAS Token"
                  onChange={onEncryptedConfigChange}
                  value={(formValues.encryptedConfig as IAzureCredentials)?.[AzureCredentialsType.SAS_TOKEN] ?? ''}
                />
              </Form.Item>
            )}
          </div>
        )}

        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={saveDisabled}>
              Save
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </>
  );
};

export default DestinationFormPage;
